// banking/src/pages/dashboard/Swap.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowRightLeft, ArrowDownToLine, ArrowUpFromLine, Clock, CheckCircle, XCircle, Eye } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { swapService, SwapRequest } from '../../services/swap.service';
import FiatToCryptoModal from '../../components/dashboard/swap/FiatToCryptoModal';
import CryptoToFiatModal from '../../components/dashboard/swap/CryptoToFiatModal';
import { apiService } from '../../services/api.service';

const SwapPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showFiatToCryptoModal, setShowFiatToCryptoModal] = useState(false);
  const [showCryptoToFiatModal, setShowCryptoToFiatModal] = useState(false);
  const [swapRequests, setSwapRequests] = useState<SwapRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [zenusBalance, setZenusBalance] = useState(0);
  const [zenusAccountId, setZenusAccountId] = useState<string | undefined>();

  // Check if user can use SWAP
  const canUseSwap = user?.accountType === 'INDIVIDUAL' || user?.accountType === 'CORPORATE';

  useEffect(() => {
    if (canUseSwap) {
      loadSwapRequests();
      loadZenusBalance();
    }
  }, [canUseSwap]);

  const loadZenusBalance = async () => {
    try {
      // Get Zenus account information
      const response = await apiService.get<any>('/providers/zenus/account');
      if (response.success && response.data) {
        setZenusBalance(response.data.balance || 0);
        setZenusAccountId(response.data.zenusAccountId);
      }
    } catch (error) {
      console.error('Error loading Zenus balance:', error);
    }
  };

  const loadSwapRequests = async () => {
    try {
      setIsLoading(true);
      const response = await swapService.getMyRequests({ limit: 50 });
      if (response.success && response.data) {
        setSwapRequests(response.data.requests || []);
      }
    } catch (error) {
      console.error('Error loading SWAP requests:', error);
      toast.error(t('swap.errors.loadingRequests') || 'Error loading SWAP requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwapSuccess = () => {
    loadSwapRequests();
    loadZenusBalance();
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      PENDING: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: <Clock className="w-4 h-4" />,
        label: t('swap.status.pending') || 'Pending'
      },
      IN_PROGRESS: {
        color: 'bg-blue-100 text-blue-800',
        icon: <ArrowRightLeft className="w-4 h-4 animate-pulse" />,
        label: t('swap.status.inProgress') || 'In Progress'
      },
      COMPLETED: {
        color: 'bg-green-100 text-green-800',
        icon: <CheckCircle className="w-4 h-4" />,
        label: t('swap.status.completed') || 'Completed'
      },
      CANCELLED: {
        color: 'bg-red-100 text-red-800',
        icon: <XCircle className="w-4 h-4" />,
        label: t('swap.status.cancelled') || 'Cancelled'
      }
    };

    const config = statusConfig[status] || statusConfig.PENDING;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
        <span>{config.label}</span>
      </span>
    );
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!canUseSwap) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-md">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
            {t('swap.notAvailable.title') || 'SWAP Not Available'}
          </h3>
          <p className="text-yellow-700">
            {t('swap.notAvailable.message') ||
              'The SWAP service is only available for INDIVIDUAL and CORPORATE accounts. Please complete your account verification to access this feature.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          <ArrowRightLeft className="inline-block w-8 h-8 mr-2" />
          {t('swap.title') || 'SWAP - Exchange FIAT ↔ CRYPTO'}
        </h1>
        <p className="text-gray-600">
          {t('swap.subtitle') || 'Exchange between your USA Account and cryptocurrency wallets'}
        </p>
      </div>

      {/* Action Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* FIAT to CRYPTO Card */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <div className="bg-blue-500 text-white p-3 rounded-full mr-4">
              <ArrowDownToLine className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-blue-900">
                {t('swap.fiatToCrypto.cardTitle') || 'FIAT → CRYPTO'}
              </h3>
              <p className="text-sm text-blue-700">
                {t('swap.fiatToCrypto.cardSubtitle') || 'Convert USD to Cryptocurrency'}
              </p>
            </div>
          </div>
          <p className="text-gray-700 mb-4 text-sm">
            {t('swap.fiatToCrypto.cardDescription') ||
              'Send USD from your USA Account and receive cryptocurrency in your wallet.'}
          </p>
          <button
            onClick={() => setShowFiatToCryptoModal(true)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md transition-colors"
          >
            {t('swap.fiatToCrypto.button') || 'Start FIAT → CRYPTO'}
          </button>
        </div>

        {/* CRYPTO to FIAT Card */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center mb-4">
            <div className="bg-green-500 text-white p-3 rounded-full mr-4">
              <ArrowUpFromLine className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-green-900">
                {t('swap.cryptoToFiat.cardTitle') || 'CRYPTO → FIAT'}
              </h3>
              <p className="text-sm text-green-700">
                {t('swap.cryptoToFiat.cardSubtitle') || 'Convert Cryptocurrency to USD'}
              </p>
            </div>
          </div>
          <p className="text-gray-700 mb-4 text-sm">
            {t('swap.cryptoToFiat.cardDescription') ||
              'Send cryptocurrency and receive USD in your USA Account.'}
          </p>
          <button
            onClick={() => setShowCryptoToFiatModal(true)}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-md transition-colors"
          >
            {t('swap.cryptoToFiat.button') || 'Start CRYPTO → FIAT'}
          </button>
        </div>
      </div>

      {/* SWAP Requests History */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {t('swap.history.title') || 'My SWAP Requests'}
        </h2>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-2">{t('common.loading') || 'Loading...'}</p>
          </div>
        ) : swapRequests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ArrowRightLeft className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>{t('swap.history.empty') || 'No SWAP requests yet. Create your first SWAP above!'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('swap.history.date') || 'Date'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('swap.history.type') || 'Type'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('swap.history.from') || 'From'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('swap.history.to') || 'To'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('swap.history.status') || 'Status'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('swap.history.ticket') || 'Ticket'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {swapRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(request.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        request.type === 'FIAT_TO_CRYPTO'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {request.type === 'FIAT_TO_CRYPTO' ? 'FIAT → CRYPTO' : 'CRYPTO → FIAT'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium">{request.fromAmount} {request.fromCurrency}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium">
                        {request.toAmount ? `${request.toAmount} ${request.toCurrency}` : `${request.toCurrency}`}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {request.ticket?.ticketNumber && (
                        <span className="text-blue-600 font-mono text-xs">
                          #{request.ticket.ticketNumber.slice(-8)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <FiatToCryptoModal
        isOpen={showFiatToCryptoModal}
        onClose={() => setShowFiatToCryptoModal(false)}
        zenusAccountId={zenusAccountId}
        zenusBalance={zenusBalance}
        onSuccess={handleSwapSuccess}
      />

      <CryptoToFiatModal
        isOpen={showCryptoToFiatModal}
        onClose={() => setShowCryptoToFiatModal(false)}
        zenusAccountId={zenusAccountId}
        onSuccess={handleSwapSuccess}
      />
    </div>
  );
};

export default SwapPage;